/**
 * Debounce function to limit the rate at which a function can fire.
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The time to wait before calling the function.
 * @returns {Function} - The debounced function.
 */
export const debounce = (func, wait) => {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
    };
};

/**
 * Utility to calculate if the text should have a "Read More" button based on line count and device type.
 *
 * @param {HTMLElement} element The HTML element containing the text.
 * @param {boolean} isReadMoreEnabled Flag to indicate if the Read More functionality is enabled.
 * @param {number} maxLinesDesktop Maximum number of lines before showing Read More on desktops.
 * @param {number} maxLinesTablet Maximum number of lines before showing Read More on tablets.
 * @param {number} maxLinesMobile Maximum number of lines before showing Read More on mobile devices.
 * @returns {boolean} Returns true if the element should have a Read More button, false otherwise.
 */
export function shouldHaveReadMore(element, isReadMoreEnabled, maxLinesDesktop = 4, maxLinesTablet = 3, maxLinesMobile = 3) {
    if (!isReadMoreEnabled) return false;

    const width = window.innerWidth;
    let maxLines = maxLinesDesktop;

    if (width < 768) {
        maxLines = maxLinesMobile;
    } else if (width >= 768 && width < 1024) {
        maxLines = maxLinesTablet;
    }

    const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
    const lines = Math.round(element.scrollHeight / lineHeight);
    return lines > maxLines;
}

/**
 * Toggle the specified class on the given element.
 *
 * @param {HTMLElement} element The DOM element on which to toggle the class.
 * @param {string} className The class to toggle.
 * @returns {boolean} Returns true if the class is added, false if it is removed.
 */
export function toggleClass(element, className) {
    const isActive = element.classList.toggle(className);
    return isActive;
}
